import type { Ref } from '@vue/composition-api';
import { computed, ref } from '@vue/composition-api';
import { useLaunchDarkly } from '@getaccept/lib-shared/src/launch-darkly/launch-darkly.composable';
import { until, watchOnce } from '@vueuse/core';

import { useFeatureStore, FeatureRestrictionHelper } from '@getaccept/feature-restriction';
import { defineStore } from 'pinia';
import { useAuthStore } from '@getaccept/lib-shared/src/internal/session/store/auth.store';
import type { SubscriptionPlan } from '@getaccept/lib-shared/src/enums/subscription-plan';
import { useSessionStore } from '../session/store/session.store';
import { loadLaunchDarkly } from '../launch-darkly';
import { useEntityStore } from '../entity/entity.store';
import { useLoadSubscription } from '../subscription/composables/load-subscription.composable';
import { useUserStore } from '../user/store/user.store';
import { useModulesStore } from '../modules/store/modules.store';
import { useSubscriptionStore } from '../subscription/store/subscription.store';

export enum Error {
  Other = 'other',
  NoSubscriptionFound = 'no-subscription-found',
  InvalidModuleAccess = 'invalid-module-access',
}

export const useRequiredAppDataStore = defineStore('required-app-data', () => {
  const sessionStore = useSessionStore();
  const authStore = useAuthStore();
  const entityStore = useEntityStore();
  const userStore = useUserStore();
  const modulesStore = useModulesStore();
  const subscriptionStore = useSubscriptionStore();
  const featureStore = useFeatureStore();

  const { loadSubscription } = useLoadSubscription();
  const { ready: isLaunchDarklyLoaded, flags } = useLaunchDarkly();

  const error: Ref<Error> = ref(null);

  const load = async () => {
    try {
      await Promise.all([authStore.fetchToken(), sessionStore.fetchSession()]);
      loadLaunchDarkly();
      await until(isLaunchDarklyLoaded).toBe(true);
      await Promise.all([
        entityStore.loadEntity(),
        userStore.fetchUser(),
        entityStore.loadAddons(),
      ]);
      if (flags.value.newSubscription) {
        await loadSubscription();
      }

      initFeatures();
    } catch (e) {
      console.error('Could not load required application data:', e);
      error.value = Error.Other;
    }
  };

  const isLoaded = computed(
    () =>
      !!authStore.token &&
      modulesStore.activeModule &&
      sessionStore.sessionIsLoaded &&
      entityStore.isLoaded &&
      userStore.loaded &&
      isLaunchDarklyLoaded.value &&
      (flags.value.newSubscription ? subscriptionStore.isLoaded : true)
  );

  watchOnce(
    () =>
      modulesStore.isInvalidModuleAccess ||
      entityStore.isLoadEntityError ||
      userStore.error ||
      subscriptionStore.isError,
    () => {
      if (modulesStore.isInvalidModuleAccess) {
        error.value = Error.InvalidModuleAccess;
        return;
      }

      if (subscriptionStore.isError) {
        error.value = Error.NoSubscriptionFound;
        return;
      }

      error.value = Error.Other;
    }
  );

  const isErrorDuringFirstLoad = computed(() => {
    if (isLoaded.value) {
      return false;
    }

    return error.value;
  });

  const initFeatures = () => {
    const featureMap = flags.value.newSubscription
      ? FeatureRestrictionHelper.getFeatureStatusMap(
          subscriptionStore.subscription.planCode,
          subscriptionStore.subscription.features
        )
      : FeatureRestrictionHelper.getLegacyFeatureStatusMap(
          sessionStore.session.plan as SubscriptionPlan,
          entityStore.addons
        );

    featureStore.setFeatures(featureMap);
  };

  const waitForIsLoaded = async () => await until(isLoaded).toBe(true);

  return {
    load,
    waitForIsLoaded,
    isLoaded,
    isErrorDuringFirstLoad,
    error,
  };
});
